/**
 * FeedImage — a feed preview image that fails gracefully.
 *
 * DEV-1420. Shared feed items store the source's raw `og:image` URL. When that
 * source is Facebook / Instagram, the CDN URL (scontent.*.fbcdn.net,
 * *.cdninstagram.com) is signed + short-lived and blocks cross-origin
 * hot-linking, so the image later 403/404s and the browser shows its broken-
 * image glyph. This wrapper swaps that ugly state for a clean one: on load
 * error (or empty src) it renders the optional `fallback` node, or nothing —
 * so the card collapses instead of showing a broken box.
 *
 * The real fix (re-hosting the image to our own storage at share time) is
 * tracked separately (DEV-1421); this is the render-boundary safety net that
 * still helps even once re-hosting lands (expired-before-rehost, fetch
 * failures, etc.).
 */
import React, { useState } from 'react';

export interface FeedImageProps {
  /** Already-resolved image src (e.g. mediaSrc(...) output). */
  src: string | null | undefined;
  className?: string;
  alt?: string;
  loading?: 'lazy' | 'eager';
  onClick?: React.MouseEventHandler<HTMLImageElement>;
  /** Rendered when src is empty or the image fails to load. Default: nothing. */
  fallback?: React.ReactNode;
}

export default function FeedImage({
  src,
  className,
  alt = '',
  loading = 'lazy',
  onClick,
  fallback = null,
}: FeedImageProps) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <>{fallback}</>;
  return (
    <img
      className={className}
      src={src}
      alt={alt}
      loading={loading}
      onClick={onClick}
      onError={() => setFailed(true)}
    />
  );
}
